import { Q } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { of } from "@nozbe/watermelondb/utils/rx";
import {
  withObservables,
  ExtractedObservables,
} from "@nozbe/watermelondb/react";
import { Page, Text } from "@towersystems/roam-common-native";
import { useEffect, useState } from "react";
import { View } from "react-native-ui-lib";
import { compose } from "recompose";
import { useActiveTerminal } from "../../../../utilities/app-state/hooks";
import PaymentMethod from "../../../../utilities/data/model/PaymentMethod";
import Sale from "../../../../utilities/data/model/Sale";
import { useGlobalSettingsProvider } from "../../../../utilities/global-settings/hooks";
import { CheckoutContext } from "../../utils/checkout";
import {
  useSaleControllerProvider,
  withSaleController,
} from "../../utils/sale-controller";

export const getObservables = ({ sale }: { sale: Sale }) => {
  return {
    balance: sale ? sale.balance : of(0),
    totals: sale ? sale.totals : of(undefined),
  };
};

interface CheckoutControllerProps
  extends ExtractedObservables<ReturnType<typeof getObservables>> {
  children?: React.ReactNode;
}

export interface CheckoutControllerPr2ops {
  children?: React.ReactNode;
}

const CheckoutControllerRenderer = ({
  children,
  balance,
  totals,
}: CheckoutControllerProps) => {
  const database = useDatabase();

  const [cashManagedPaymentMethod, setCashManagedPaymentMethod] = useState<
    PaymentMethod | undefined
  >(undefined);

  const [roundingPaymentMethod, setRoundingPaymentMethod] = useState<
    PaymentMethod | undefined
  >(undefined);

  const [giftVoucherPaymentMethod, setGiftVoucherPaymentMethod] = useState<
    PaymentMethod | undefined
  >(undefined);

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const { sale } = useSaleControllerProvider();
  const { terminal } = useActiveTerminal();
  const globalSettings = useGlobalSettingsProvider();

  useEffect(() => {
    if (terminal) {
      database
        .get<PaymentMethod>("payment_methods")
        .query(
          Q.on("pay_types", "internal", Q.eq(false)),
          Q.and(Q.where("enabled", Q.eq(true))),
          Q.and(Q.where("deleted_at", Q.eq(null)))
        )
        .fetch()
        .then((i) => {
          setPaymentMethods(
            i.filter(
              (j) =>
                j.enabled &&
                (terminal.paymentMethodIds.includes(j.id) ||
                  j.id == cashManagedPaymentMethod?.id)
            )
          );
        })
        .catch((e) => console.log(e));
    }
  }, [terminal?.id, cashManagedPaymentMethod?.id]);

  useEffect(() => {
    if (globalSettings.cashManagedPaymentMethodId) {
      database
        .get<PaymentMethod>("payment_methods")
        .find(globalSettings.cashManagedPaymentMethodId)
        .then((i) => setCashManagedPaymentMethod(i))
        .catch((e) => console.log(e));
    }

    database
      .get<PaymentMethod>("payment_methods")
      .query(Q.on("pay_types", "id", Q.eq("2")))
      .fetch()
      .then((i) => setRoundingPaymentMethod(i[0]))
      .catch((e) => console.log(e));

    database
      .get<PaymentMethod>("payment_methods")
      .query(Q.on("pay_types", "id", Q.eq("35")))
      .fetch()
      .then((i) => setGiftVoucherPaymentMethod(i[0]))
      .catch((e) => console.log(e));
  }, []);

  if (
    !terminal ||
    !roundingPaymentMethod ||
    !cashManagedPaymentMethod ||
    !giftVoucherPaymentMethod
  ) {
    return (
      <Page title="Oops!">
        <View>
          <Text>Something went wrong, please try again later.</Text>
        </View>
      </Page>
    );
  }

  // screen is likely transitioning
  if (!sale || !totals || balance === undefined) {
    return null;
  }

  return (
    <CheckoutContext.Provider
      value={{
        sale,
        balance: balance,
        terminal,
        selectablePaymentMethods:
          globalSettings.giftCardsEnabled && giftVoucherPaymentMethod
            ? [...paymentMethods, giftVoucherPaymentMethod]
            : paymentMethods,
        roundingPaymentMethod,
        cashManagedPaymentMethod,
        giftVoucherPaymentMethod,
        totals,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export const CheckoutController = compose(
  withSaleController,
  withObservables(["sale"], getObservables)
  // @ts-ignore
)(CheckoutControllerRenderer);
